import { useState } from 'react';
import { Scale, ChevronDown, ChevronUp, Pencil } from 'lucide-react';
import { bodyweightEffect, bodyweightTrend } from '@trackmind/core';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useApp } from '@/context/AppContext';
import { useToday } from '@/hooks/useToday';
import { cn } from '@/lib/utils';

interface BodyweightWidgetProps {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

/**
 * The morning weigh-in, where the morning starts: on Today.
 *
 * Same record as Fitness → Bodyweight — one per day, synced, written through
 * core's `bodyweightEffect` — so logging here or there is the same thing.
 * Deliberately imports nothing from the Fitness view: Today is in the initial
 * bundle and Fitness is lazy-loaded.
 *
 * One line once you've weighed in (weight · change over the week · edit);
 * an input until you have. Same composition at both widths — it is already
 * a single row.
 */
export function BodyweightWidget({ isCollapsed, onToggleCollapse }: BodyweightWidgetProps) {
  const { fitnessBodyweights, fitnessSettings, saveFitnessBodyweight } = useApp();
  const todayKey = useToday();
  const today = todayKey();
  const unit = fitnessSettings.unit;
  const trend = bodyweightTrend(fitnessBodyweights, unit, today);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const n = Number(value);
  const valid = Number.isFinite(n) && n > 0 && n < 1000;
  const showInput = editing || !trend?.loggedToday;

  const save = async () => {
    if (!valid) return;
    await saveFitnessBodyweight(bodyweightEffect(fitnessBodyweights, todayKey(), n, unit));
    setValue('');
    setEditing(false);
  };

  return (
    <Card className="bg-card/50" data-testid="bodyweight-widget">
      <CardHeader className="py-2 px-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Scale className="h-4 w-4" />
            Bodyweight
          </CardTitle>
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={onToggleCollapse} aria-label={isCollapsed ? 'Expand bodyweight' : 'Collapse bodyweight'}>
            {isCollapsed ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />}
          </Button>
        </div>
      </CardHeader>

      {!isCollapsed && (
        <CardContent className="py-2 px-3 space-y-2">
          {trend && (
            <div className="flex flex-wrap items-center justify-between gap-x-2 text-sm">
              <span>
                <span className="font-semibold tabular-nums">{trend.latest} {unit}</span>
                <span className="text-xs text-muted-foreground"> · {trend.loggedToday ? 'today' : `last on ${trend.latestDate.slice(5)}`}</span>
              </span>
              <span className="flex items-center gap-1">
                {trend.change !== null && (
                  <span className={cn('text-xs tabular-nums', trend.change < 0 ? 'text-green-600' : trend.change > 0 ? 'text-amber-600' : 'text-muted-foreground')}>
                    {trend.change > 0 ? '+' : ''}{trend.change} {unit} since {trend.sinceDate?.slice(5)}
                  </span>
                )}
                {trend.loggedToday && !editing && (
                  <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Change today's weight" onClick={() => { setValue(String(trend.latest)); setEditing(true); }}>
                    <Pencil className="h-3 w-3" />
                  </Button>
                )}
              </span>
            </div>
          )}
          {showInput && (
            <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); void save(); }}>
              <Input
                inputMode="decimal"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={`Today's weight, ${unit}`}
                aria-label={`Today's weight in ${unit}`}
                className="h-10"
              />
              <Button type="submit" variant="outline" className="h-10" disabled={!valid}>Log</Button>
            </form>
          )}
        </CardContent>
      )}
    </Card>
  );
}
